import { InspectionLifecyclePanel } from "../../components/site-workflows/InspectionLifecycle"
import { PhysicalLinkPanel } from "../../components/physical/PhysicalLinkPanel"
import {
  DeleteOutlined,
  DoubleRightOutlined,
  FileAddOutlined,
  FilePdfOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  SaveOutlined,
} from "@ant-design/icons"
import {
  Alert,
  AutoComplete,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Input,
  Popconfirm,
  Row,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from "antd"
import dayjs from "dayjs"
import { FC, ReactElement, SetStateAction, useCallback, useEffect, useMemo, useState } from "react"
import { Link, useHistory, useLocation } from "react-router-dom"
import { v4 as uuidv4 } from "uuid"
import {
  CatalogAssignment,
  EquipmentProgress,
  TemplateSelector,
} from "../../components/en1176/CatalogAssignment"
import {
  useRegulatoryAnswers,
  useRegulatoryChildren,
  useRegulatoryEntitySave,
  useRegulatoryRequest,
  useRegulatorySite,
} from "../../components/en1176/useRegulatoryAnswers"
import { StorageRequest, UCPayloadType } from "../../generated/proto-ts/main"
import { assignRevision, legacyControls, regulatoryUuid } from "../../utils/regulatoryPresentation"
import { useAssignedCatalog, useRegulatoryTemplates } from "../Regulatory/useOperationalCatalog"

import { FlexCol, FlexRow, UnderlinedSectionTitle } from "../../components/commons-ts/common"
import { EntityTimestamps } from "../../components/commons-ts/entityTimestamps"
import { GoogleAddressWidget } from "../../components/commons-ts/googleMaps"
import { Label } from "../../components/commons-ts/input"
import { SimplifiedBreadcrumb } from "../../components/commons-ts/simplifiedBreadcrumb"
import { TableImageThumbnail } from "../../components/commons-ts/tableImageThumbnail"
import { UploadImageListV2 } from "../../components/commons-ts/uploadImageV2"
import {
  Site,
  Annex,
  Equipment,
  NormType,
  StorageEntityType,
  UUID,
} from "../../generated/proto-ts/main"
import { Translated } from "../../utils/translated"
import {
  CENTRAL_CONTAINER_GAP,
  COLOR_BG_ROW_DELETED,
  parsePathForEntityUUID,
  pbUUIDToUuid,
  uuidToPbUUID,
} from "../../utils/utils"
import { AnnexWidget } from "../01_Site"

import { EN1176QuestionnaireWidget } from "../../components/en1176/EN1176QuestionnaireWidget"

import { useSyncronizationContext } from "../../providers/syncronizationProvider"
import { useTranslationContext } from "../../providers/translationProvider"
import { ICloudCustomer, ICloudUser } from "../../types"
import {
  useUsercommCreateAnnexBimodal,
  useUsercommDeleteSiteBimodal,
  useUsercommSiteAnnexesBimodal,
} from "../../usercomm/common/usercommAsyncRequestBimodal"
import { callCloudApiV2 } from "../../utils/cloudApiV2"

export const EN1176SitePage: FC = () => {
  const [antdMessageCtx, contextHolder] = message.useMessage()
  const location = useLocation()
  const history = useHistory()
  const { translated } = useTranslationContext()
  const { currentEnterprise, isAdmin } = useSyncronizationContext()

  const memoSiteUUID = useMemo((): UUID | null => {
    let siteUUID = parsePathForEntityUUID(location.pathname)
    if (siteUUID === null) return null
    return uuidToPbUUID(siteUUID)
  }, [location.pathname])

  const memoSiteUUIDStr = useMemo(() => {
    return memoSiteUUID ? pbUUIDToUuid(memoSiteUUID) : null
  }, [memoSiteUUID])

  // Site Bimodal Hooks
  const [site, getSite, externallySetSite, entityError] = useRegulatorySite()
  const entitySave = useRegulatoryEntitySave()
  const [deleteSiteAck, deleteSite] = useUsercommDeleteSiteBimodal()
  const [siteEquipmentsMap, , , getSiteChildrenRecursive, childrenError] = useRegulatoryChildren()
  const [siteAnnexes, getSiteAnnexes] = useUsercommSiteAnnexesBimodal()
  const [createdAnnexUUID, createAnnexRequest] = useUsercommCreateAnnexBimodal()
  const { request: createRequest } = useRegulatoryRequest()

  const answerResource = useRegulatoryAnswers(memoSiteUUIDStr ?? "", StorageEntityType.SITE)
  const siteAnswers = answerResource.answers
  // Site Fields
  const [siteName, setSiteName] = useState<string | null>(null)
  const [clientName, setClientName] = useState<string | null>(null)
  const [missionName, setMissionName] = useState<string | null>(null)
  const [address, setAddress] = useState<string | null>(null)
  const [executionDate, setExecutionDate] = useState<dayjs.Dayjs | null>(null)
  const [pictures, setPictures] = useState<UUID[] | null>(null)
  const [userUUID, setUserUUID] = useState<UUID | null>(null)
  const [createdAt, setCreatedAt] = useState<number | null>(null)

  // Cloud users & customers
  const [enterpriseCustomers, setEnterpriseCustomers] = useState<ICloudCustomer[]>([])
  const [enterpriseUsers, setEnterpriseUsers] = useState<ICloudUser[]>([])
  const [siteUser, setSiteUser] = useState<ICloudUser | null>(null)
  const [selectedUserUUID, setSelectedUserUUID] = useState<string | null>(null)

  const templateResource = useRegulatoryTemplates()
  const regulatoryControlTemplates = templateResource.templates.filter((t) => t.Scope !== "site")
  const assignedUUID = site ? regulatoryUuid(site.regulatory_template_uuid) : ""
  const catalog = useAssignedCatalog(
    "site",
    site ? regulatoryUuid(site.uuid) : "",
    site ? `${assignedUUID}/${regulatoryUuid(site.regulatory_template_revision_uuid)}` : "",
  )
  const [selectedRevision, setSelectedRevision] = useState<string | undefined>()
  const regulatoryControlTemplate = catalog.template
  const templateControlPoints = catalog.points
  const [selectedSiteTemplateUUID, setSelectedSiteTemplateUUID] = useState<string | null>(null)
  const [selectedRegulatoryTemplateUUID, setSelectedRegulatoryTemplateUUID] = useState<
    string | null
  >(null)
  const [newEquipmentName, setNewEquipmentName] = useState<string>("")
  const [isCreatingEquipment, setIsCreatingEquipment] = useState<boolean>(false)

  // Load Site
  useEffect(() => {
    if (memoSiteUUID === null) return
    getSite(memoSiteUUID)
  }, [memoSiteUUID])

  // Set Site State
  useEffect(() => {
    if (site === null) return
    setSelectedRevision(regulatoryUuid(site.regulatory_template_revision_uuid) || undefined)
    setSelectedSiteTemplateUUID(regulatoryUuid(site.regulatory_template_uuid) || null)
    setSiteName(site.site_name)
    setClientName(site.client_name)
    setMissionName(site.mission_name)
    setAddress(site.address)
    setPictures(site.pictures)
    setUserUUID(site.user_uuid ?? null)
    setCreatedAt(site.created_at)
    if (site.execution_date === 0) {
      setExecutionDate(null)
    } else {
      setExecutionDate(dayjs(site.execution_date))
    }
    if (site.user_uuid) {
      setSelectedUserUUID(pbUUIDToUuid(site.user_uuid))
    }
  }, [site])

  // Load Site Children (Equipments & Annexes) & Answers
  useEffect(() => {
    if (memoSiteUUID === null) return
    getSiteChildrenRecursive(memoSiteUUID)
    getSiteAnnexes(memoSiteUUID)
  }, [memoSiteUUID])

  // Equipments List
  const equipments = useMemo(() => {
    if (siteEquipmentsMap === null || memoSiteUUIDStr === null) return []
    return siteEquipmentsMap[memoSiteUUIDStr] || []
  }, [siteEquipmentsMap, memoSiteUUIDStr])

  // Create Equipment
  const onCreateEquipment = useCallback(async () => {
    if (memoSiteUUID === null || !newEquipmentName || !selectedRegulatoryTemplateUUID) return
    setIsCreatingEquipment(true)
    const ts = Date.now()
    const newEquip = new Equipment({
      uuid: uuidToPbUUID(uuidv4()),
      site_uuid: memoSiteUUID,
      regulatory_template_uuid: uuidToPbUUID(selectedRegulatoryTemplateUUID),
      equipment_name: newEquipmentName,
      created_at: ts,
      updated_at: ts,
    })
    try {
      const payload = await createRequest(
        UCPayloadType.SICO_STORAGE_CREATE_ENTITY,
        new StorageRequest({
          entity_type: StorageEntityType.EQUIPMENT,
          parent_uuid: memoSiteUUID,
          entity_uuid: newEquip.uuid,
          single_entity_data: newEquip.serializeBinary(),
        }),
      )
      const created = UUID.deserializeBinary(payload.data)
      antdMessageCtx.success("Équipement créé.")
      setNewEquipmentName("")
      history.push(`/regulatory/equipments/${pbUUIDToUuid(created)}`)
    } catch (reason) {
      antdMessageCtx.error(reason instanceof Error ? reason.message : String(reason))
    } finally {
      setIsCreatingEquipment(false)
    }
  }, [memoSiteUUID, newEquipmentName, selectedRegulatoryTemplateUUID])

  // Annexes Add
  const onAddAnnex = useCallback(() => {
    if (memoSiteUUID === null) return
    const ts = Date.now()
    const newAnnex = new Annex({
      uuid: uuidToPbUUID(uuidv4()),
      site_uuid: memoSiteUUID,
      created_at: ts,
      updated_at: ts,
    })
    createAnnexRequest(memoSiteUUID, newAnnex)
  }, [memoSiteUUID])

  useEffect(() => {
    if (createdAnnexUUID === null || memoSiteUUID === null) return
    getSiteAnnexes(memoSiteUUID)
  }, [createdAnnexUUID])

  // Cloud API Data (User)
  useEffect(() => {
    if (!userUUID) return
    callCloudApiV2<ICloudUser>(`/user?user_uuid=${pbUUIDToUuid(userUUID)}`).then(({ entity }) => {
      if (entity) setSiteUser(entity)
    })
  }, [userUUID])

  // Cloud API Data (Enterprise: customers, users, templates)
  useEffect(() => {
    if (currentEnterprise === null) return
    callCloudApiV2<ICloudCustomer[]>(
      `/enterprise/customers?enterprise_uuid=${currentEnterprise.UUID}`,
    ).then(({ entity }) => {
      if (entity) setEnterpriseCustomers(entity)
    })
    callCloudApiV2<ICloudUser[]>(
      `/enterprise/users?enterprise_uuid=${currentEnterprise.UUID}`,
    ).then(({ entity }) => {
      if (entity) setEnterpriseUsers(entity)
    })
  }, [currentEnterprise])

  // Mutation check & Update Site
  const memoSiteIsMutated = useMemo(() => {
    if (site === null || createdAt === null) return false
    const prevUserStr = pbUUIDToUuid(site.user_uuid)
    const currUserStr = userUUID ? pbUUIDToUuid(userUUID) : null
    let execDateInt = executionDate ? executionDate.toDate().getTime() : 0

    let mutated =
      site.site_name !== (siteName || "") ||
      selectedRevision !== (regulatoryUuid(site.regulatory_template_revision_uuid) || undefined) ||
      (regulatoryUuid(site.regulatory_template_uuid) || null) !== selectedSiteTemplateUUID ||
      site.client_name !== (clientName || "") ||
      site.mission_name !== (missionName || "") ||
      site.address !== (address || "") ||
      site.execution_date !== execDateInt ||
      prevUserStr !== currUserStr

    if (pictures !== null) {
      const prevPics = site.pictures.map((u) => pbUUIDToUuid(u)).join(",")
      const currPics = pictures.map((u) => pbUUIDToUuid(u)).join(",")
      if (prevPics !== currPics) mutated = true
    }
    return mutated
  }, [
    site,
    siteName,
    selectedSiteTemplateUUID,
    selectedRevision,
    clientName,
    missionName,
    address,
    executionDate,
    pictures,
    userUUID,
    createdAt,
  ])

  const onUpdateSite = async () => {
    if (site === null) return
    const updated = site.clone()
    updated.site_name = siteName ?? ""
    updated.client_name = clientName ?? ""
    updated.mission_name = missionName ?? ""
    if (selectedSiteTemplateUUID)
      updated.regulatory_template_uuid = uuidToPbUUID(selectedSiteTemplateUUID)
    assignRevision(updated, selectedSiteTemplateUUID !== assignedUUID, selectedRevision)
    updated.norm_type = NormType.EN_1176
    if (executionDate) updated.execution_date = executionDate.toDate().getTime()
    if (address) updated.address = address
    if (userUUID) updated.user_uuid = userUUID
    if (pictures) updated.pictures = pictures
    updated.updated_at = Date.now()

    if (answerResource.dirty) {
      antdMessageCtx.warning("Attendez la confirmation des réponses avant de modifier la fiche.")
      return
    }
    try {
      const stored = await entitySave.saveEntity(updated, StorageEntityType.SITE)
      externallySetSite(Site.deserializeBinary(stored))
      catalog.reload()
      antdMessageCtx.success("Fiche enregistrée.")
    } catch (reason) {
      antdMessageCtx.error(reason instanceof Error ? reason.message : String(reason))
    }
  }

  const onPicturesChange = useCallback(
    (nextPictures: SetStateAction<UUID[] | null>) => setPictures(nextPictures),
    [],
  )

  const onSitePictureChange = useCallback(
    (index: number, nextPictures: SetStateAction<UUID[] | null>) => {
      onPicturesChange((currentPictures) => {
        const currentSlot = currentPictures?.slice(index, index + 1) ?? []
        const nextSlot =
          typeof nextPictures === "function" ? nextPictures(currentSlot) : nextPictures
        const flattenedPictures = currentPictures?.slice(0, 2) ?? []
        flattenedPictures.splice(index, 1, ...(nextSlot ?? []).slice(0, 1))
        return flattenedPictures
      })
    },
    [onPicturesChange],
  )

  const onDeleteSite = useCallback(() => {
    if (memoSiteUUID === null) return
    deleteSite(memoSiteUUID)
  }, [memoSiteUUID])

  useEffect(() => {
    if (deleteSiteAck) {
      antdMessageCtx.info(translated("Site supprimé !"))
      history.push("/")
    }
  }, [deleteSiteAck])

  // Annexes Renderer
  const memoAnnexesElements = useMemo(() => {
    if (siteAnnexes === null) return null
    const children: ReactElement[] = []
    const validAnnexes = siteAnnexes
      .filter((a) => a.deleted_at === 0)
      .sort((a, b) => a.created_at - b.created_at)

    validAnnexes.forEach((annex, i) => {
      children.push(
        <AnnexWidget
          annex={annex}
          idx={i + 1}
          getAnnexes={() => memoSiteUUID && getSiteAnnexes(memoSiteUUID)}
          key={pbUUIDToUuid(annex.uuid)}
        />,
      )
      if (i !== validAnnexes.length - 1) {
        children.push(<Divider key={`div-${i}`} />)
      }
    })
    return <div style={{ width: "100%" }}>{children}</div>
  }, [siteAnnexes, memoSiteUUID])

  // Table of Equipments for EN 1176
  const equipmentsColumns = useMemo(() => {
    return [
      {
        title: "📷",
        width: "3.5rem",
        render: (_: any, equip: Equipment) => {
          const picUuid = equip.pictures[0]
          if (!picUuid) return <div style={{ width: "3rem" }} />
          return (
            <Link to={`/regulatory/equipments/${pbUUIDToUuid(equip.uuid)}`}>
              <TableImageThumbnail pictureUUID={picUuid} alt={equip.equipment_name} />
            </Link>
          )
        },
      },
      {
        title: <Translated keyEn="Équipement" />,
        dataIndex: "equipment_name",
        render: (name: string, equip: Equipment) => (
          <Link
            to={`/regulatory/equipments/${pbUUIDToUuid(equip.uuid)}`}
            style={{ fontWeight: "bold", textDecoration: "none" }}
          >
            {name || "Sans nom"}
          </Link>
        ),
      },
      {
        title: "Modèles / état d’inspection",
        render: (_: any, equip: Equipment) => {
          return (
            <EquipmentProgress
              uuid={pbUUIDToUuid(equip.uuid)}
              templateUuid={regulatoryUuid(equip.regulatory_template_uuid)}
            />
          )
        },
      },
      {
        title: "",
        width: "3rem",
        render: (_: any, equip: Equipment) => (
          <Link to={`/regulatory/equipments/${pbUUIDToUuid(equip.uuid)}`}>
            <Button type="link" size="small">
              <DoubleRightOutlined />
            </Button>
          </Link>
        ),
      },
    ]
  }, [regulatoryControlTemplates])

  // Regulatory Control Template Options for Select

  if (entityError || childrenError)
    return (
      <Alert
        type="error"
        showIcon
        message={entityError || childrenError}
        action={<Button onClick={() => window.location.reload()}>Réessayer</Button>}
      />
    )

  if (site === null) {
    return (
      <FlexCol
        style={{ width: "100%", height: "60vh", justifyContent: "center", alignItems: "center" }}
      >
        <Spin size="large" />
      </FlexCol>
    )
  }

  return (
    <>
      {contextHolder}
      <FlexCol
        className="regulatory-operational"
        style={{ width: "100%", gap: CENTRAL_CONTAINER_GAP }}
      >
        <SimplifiedBreadcrumb
          root="/"
          previousItems={[{ label: "Accueil", href: "/" }]}
          currentItem={{ label: siteName || "Inspection EN 1176" }}
        />
        <PhysicalLinkPanel id={memoSiteUUIDStr ?? ""} />
        <InspectionLifecyclePanel inspection={memoSiteUUIDStr ?? ""} />

        {/* Page Title & Badge */}
        <Row justify="space-between" align="middle" style={{ marginTop: "0.5rem" }}>
          <Col>
            <FlexCol style={{ gap: 0 }}>
              <Label>
                <Translated keyEn="Aire de jeux" />
              </Label>
              <Typography.Text style={{ fontSize: "2rem", fontWeight: "bold" }}>
                {siteName}
              </Typography.Text>
            </FlexCol>
          </Col>
          <Col>
            <Tag
              color="purple"
              style={{
                fontSize: "1rem",
                padding: "8px 16px",
                borderRadius: "8px",
                fontWeight: "bold",
              }}
            >
              <SafetyCertificateOutlined /> NORME EN 1176 (Contrôle Sécurité)
            </Tag>
          </Col>
        </Row>

        {/* Site Name Input */}
        <FlexCol style={{ gap: 4 }}>
          <Label>
            <Translated keyEn="Nom du site / Aire de jeux" />
          </Label>

          <Input
            size="large"
            value={siteName ?? ""}
            onChange={(e) => setSiteName(e.target.value)}
            placeholder="Ex: Aire de jeux du Parc Municipal"
          />
        </FlexCol>

        {/* Site Metadata & Annexes */}
        <UnderlinedSectionTitle>
          <Translated keyEn="Informations administratives" />
        </UnderlinedSectionTitle>
        <Row gutter={[12, 12]}>
          <Col xs={24} md={12}>
            <FlexCol style={{ gap: 4 }}>
              <Label>
                <Translated keyEn="Nom de la mission" />
              </Label>
              <Input
                size="large"
                value={missionName ?? ""}
                onChange={(e) => setMissionName(e.target.value)}
                placeholder="Ex: Inspection Annuelle 2026"
              />
            </FlexCol>
          </Col>
          <Col xs={24} md={12}>
            <FlexCol style={{ gap: 4 }}>
              <Label>
                <Translated keyEn="Date d'exécution" />
              </Label>
              <DatePicker
                size="large"
                style={{ width: "100%" }}
                value={executionDate}
                onChange={(val) => setExecutionDate(val)}
              />
            </FlexCol>
          </Col>
          <Col xs={24}>
            <Label>
              <Translated keyEn="Client" />
            </Label>
            <AutoComplete
              size="large"
              value={clientName ?? ""}
              style={{ width: "100%" }}
              options={enterpriseCustomers.map((c) => ({ value: c.Name }))}
              onChange={(val) => setClientName(val)}
              placeholder="Sélectionner ou saisir le nom du client"
            />
          </Col>
          <Col xs={24} md={24}>
            <FlexCol style={{ gap: 4 }}>
              <Label>
                <Translated keyEn="Adresse du site" />
              </Label>
              <GoogleAddressWidget address={address} setAddress={setAddress} points={[]} />
            </FlexCol>
          </Col>
          <Col xs={24} md={12}>
            <FlexCol style={{ gap: 4 }}>
              <Label>
                <Translated keyEn="Photo générale du site" />
              </Label>
              {pictures !== null && (
                <UploadImageListV2
                  pictures={pictures.slice(0, 1)}
                  setPictures={(nextPictures) => onSitePictureChange(0, nextPictures)}
                  maxCount={1}
                />
              )}
            </FlexCol>
          </Col>
          <Col xs={24} md={12}>
            <FlexCol style={{ gap: 4 }}>
              <Label>
                <Translated keyEn="Photo d'affichage obligatoire" />
              </Label>
              {pictures !== null && (
                <UploadImageListV2
                  pictures={pictures.slice(1, 2)}
                  setPictures={(nextPictures) => onSitePictureChange(1, nextPictures)}
                  maxCount={1}
                />
              )}
            </FlexCol>
          </Col>
        </Row>

        {/* 3. Equipments Table */}
        <UnderlinedSectionTitle>
          <Translated keyEn="Équipements du site" />
        </UnderlinedSectionTitle>
        <Card size="small" style={{ borderRadius: 10, backgroundColor: "#fafafa" }}>
          <Row gutter={[12, 12]} align="bottom">
            <Col xs={24} md={12}>
              <Label>
                <Translated keyEn="Nom de l'équipement" />
              </Label>
              <Input
                size="large"
                value={newEquipmentName}
                onChange={(e) => setNewEquipmentName(e.target.value)}
                placeholder="Ex: Toboggan Grande Tour, Balançoire 2 Sièges..."
              />
            </Col>
            <Col xs={24} md={8}>
              <Label>
                <Translated keyEn="Modèle d'inspection (Norme)" />
              </Label>
              <TemplateSelector
                value={selectedRegulatoryTemplateUUID}
                onChange={setSelectedRegulatoryTemplateUUID}
                disabled={answerResource.dirty || entitySave.saving}
              />
            </Col>
            <Col xs={24} md={4}>
              <Button
                type="primary"
                size="large"
                icon={<PlusOutlined />}
                block
                disabled={!newEquipmentName}
                loading={isCreatingEquipment}
                onClick={onCreateEquipment}
              >
                <Translated keyEn="Ajouter" />
              </Button>
            </Col>
          </Row>
        </Card>
        <Table
          scroll={{ x: 760 }}
          dataSource={equipments}
          columns={equipmentsColumns}
          rowKey={(e) => pbUUIDToUuid(e.uuid)}
          pagination={false}
          size="small"
          bordered
          onRow={(record) => {
            if (record.deleted_at === 0) return {}
            return isAdmin
              ? { style: { backgroundColor: COLOR_BG_ROW_DELETED } }
              : { style: { display: "none" } }
          }}
        />

        {/* 1. Site-wide Control Points Questionnaire */}
        <UnderlinedSectionTitle>
          <Translated keyEn="Points de contrôle généraux du site" />
        </UnderlinedSectionTitle>
        <CatalogAssignment
          catalog={catalog}
          assigned={assignedUUID}
          selected={selectedSiteTemplateUUID}
          onChange={(value) => {
            setSelectedSiteTemplateUUID(value)
            setSelectedRevision(undefined)
          }}
          selectedRevision={selectedRevision}
          onRevisionChange={setSelectedRevision}
          scope="site"
          disabled={answerResource.dirty || entitySave.saving}
        />
        <EN1176QuestionnaireWidget
          controlPoints={
            catalog.data?.resolved ? templateControlPoints : legacyControls(siteAnswers)
          }
          saveStates={answerResource.states}
          retry={answerResource.retry}
          loading={answerResource.loading || catalog.loading}
          error={answerResource.error}
          reload={answerResource.reload}
          connected={answerResource.connected}
          readOnly={!catalog.data?.resolved || !!catalog.error}
          answers={siteAnswers}
          onAnswersChange={answerResource.save}
          onAnswerBlur={answerResource.flush}
          title={<Translated keyEn="Exigences globales du site" />}
        />

        {/* Annexes */}
        <UnderlinedSectionTitle>
          <Translated keyEn="Annexes du rapport" />
        </UnderlinedSectionTitle>
        <FlexCol style={{ alignItems: "start", gap: 8 }}>
          {memoAnnexesElements}
          <Button type="primary" size="large" onClick={onAddAnnex} icon={<FileAddOutlined />}>
            <Translated keyEn="Ajouter une annexe" />
          </Button>
        </FlexCol>

        {/* Save, Report & Delete Toolbar */}
        <FlexRow
          style={{
            justifyContent: "flex-end",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 12,
            marginTop: 20,
          }}
        >
          <Popconfirm
            title={<Translated keyEn="Êtes-vous sûr de vouloir supprimer ce site ?" />}
            onConfirm={onDeleteSite}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              <Translated keyEn="Supprimer" />
            </Button>
          </Popconfirm>

          <Button
            type="primary"
            icon={<SaveOutlined />}
            size="large"
            disabled={!memoSiteIsMutated || entitySave.saving || answerResource.dirty}
            loading={entitySave.saving}
            onClick={onUpdateSite}
          >
            <Translated keyEn="Enregistrer" />
          </Button>

          <Link to={`/regulatory/report/${memoSiteUUIDStr}`}>
            <Button
              type="primary"
              icon={<FilePdfOutlined />}
              size="large"
              style={{ backgroundColor: "#722ed1" }}
            >
              <Translated keyEn="Rapport EN 1176" />
            </Button>
          </Link>
        </FlexRow>

        <EntityTimestamps entity={site} />
      </FlexCol>
    </>
  )
}
